/**
 * Safe formula evaluator for form calculated fields and calc_table cells.
 *
 * Supports:
 *   - Math operators: + - * / ( ) % **
 *   - Math.* functions (Math.round, Math.floor, Math.max, Math.min, Math.abs, Math.sqrt, Math.pow)
 *   - Numeric constants (e.g., 703, 2.54)
 *   - Field substitution:  {field_id}  → replaced with parseFloat of that response
 *   - Cross-field references into calc_tables (read from another field's response):
 *       {tableId.col.<col_key>.sum|avg|count|min|max}
 *       {tableId.row.<row_index>.<col_key>}
 *   - Table cell substitution (inside calc_table cells):
 *       row.<col_key>   → current row's cell value
 *       col.<col_key>.sum / .avg / .count / .min / .max
 *   - Aggregation helpers: sum(), avg(), count(), min(), max() over provided arrays
 *   - Conditional: ternary  a > b ? x : y
 *   - Date helpers (pre-evaluated before eval):
 *       AGE({dob_field})          → whole years from DOB (ISO date) to today
 *       DAYS_BETWEEN({d1},{d2})   → whole days between two ISO dates (d2 - d1)
 *       TODAY()                    → today's ISO date — but this evaluator only returns numbers;
 *                                    for date math, use it inside DAYS_BETWEEN
 *
 * Rejects anything that looks unsafe (access to globals, function constructors, etc.).
 */

const DISALLOWED = /\b(window|document|globalThis|process|require|import|eval|Function|setTimeout|setInterval|fetch|XMLHttpRequest|localStorage|sessionStorage|__proto__|constructor|prototype)\b/;

function safeNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-+eE]/g, ''));
  return isFinite(n) ? n : 0;
}

// Parse an ISO-ish date (YYYY-MM-DD or full ISO) → Date (or null)
function safeDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// --- Cross-table reference substitution ---
// {tableId.col.colKey.agg}   → aggregate across all rows of that calc_table
// {tableId.row.N.colKey}     → specific row's cell
function substituteTableRefs(expr, fields) {
  // Column aggregate: {tableId.col.colKey.sum}
  expr = expr.replace(/\{([a-zA-Z0-9_-]+)\.col\.([a-zA-Z0-9_]+)\.(sum|avg|count|min|max)\}/g, (_, tid, key, agg) => {
    const tbl = fields[tid];
    const rows = (tbl && Array.isArray(tbl.rows)) ? tbl.rows : [];
    const nums = rows.map(r => safeNumber(r?.[key])).filter(n => !isNaN(n));
    if (agg === 'sum') return String(nums.reduce((a, b) => a + b, 0));
    if (agg === 'avg') return String(nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0);
    if (agg === 'count') return String(nums.length);
    if (agg === 'min') return String(nums.length ? Math.min(...nums) : 0);
    if (agg === 'max') return String(nums.length ? Math.max(...nums) : 0);
    return '0';
  });
  // Specific row cell: {tableId.row.0.colKey}
  expr = expr.replace(/\{([a-zA-Z0-9_-]+)\.row\.(\d+)\.([a-zA-Z0-9_]+)\}/g, (_, tid, rIdx, key) => {
    const tbl = fields[tid];
    const rows = (tbl && Array.isArray(tbl.rows)) ? tbl.rows : [];
    const r = rows[parseInt(rIdx, 10)];
    return String(safeNumber(r?.[key]));
  });
  return expr;
}

// --- Date helper substitution ---
// AGE({dob}) and DAYS_BETWEEN({a},{b}) — substituted AFTER field refs have been resolved
// so we can peek at the raw string values via fields map BEFORE they're numeric-coerced.
function substituteDateHelpers(expr, fields) {
  // AGE({field_id})  → whole years from DOB to today
  expr = expr.replace(/AGE\(\s*\{([a-zA-Z0-9_-]+)\}\s*\)/g, (_, fid) => {
    const d = safeDate(fields[fid]);
    if (!d) return '0';
    const today = new Date();
    let years = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years--;
    return String(Math.max(0, years));
  });
  // DAYS_BETWEEN({a}, {b})  → whole days between two ISO dates (b - a)
  expr = expr.replace(/DAYS_BETWEEN\(\s*\{([a-zA-Z0-9_-]+)\}\s*,\s*\{([a-zA-Z0-9_-]+)\}\s*\)/g, (_, a, b) => {
    const da = safeDate(fields[a]);
    const db = safeDate(fields[b]);
    if (!da || !db) return '0';
    const ms = db.getTime() - da.getTime();
    return String(Math.round(ms / 86400000));
  });
  return expr;
}

// Replaces {field_id} with parseFloat value from scope.fields[field_id]
// Only replaces BARE refs (no dot). Dot-refs are handled by substituteTableRefs.
function substituteFieldRefs(expr, fields) {
  return expr.replace(/\{([a-zA-Z0-9_-]+)\}/g, (_, key) => {
    const val = fields[key];
    return String(safeNumber(val));
  });
}

// Replaces row.<colKey> with current row value
function substituteRowRefs(expr, row) {
  return expr.replace(/\brow\.([a-zA-Z0-9_]+)\b/g, (_, key) => String(safeNumber(row[key])));
}

// Replaces col.<colKey>.<agg> with aggregate across all rows
function substituteColRefs(expr, allRows) {
  return expr.replace(/\bcol\.([a-zA-Z0-9_]+)\.(sum|avg|count|min|max)\b/g, (_, key, agg) => {
    const nums = (allRows || []).map(r => safeNumber(r[key])).filter(n => !isNaN(n));
    if (agg === 'sum') return String(nums.reduce((a, b) => a + b, 0));
    if (agg === 'avg') return String(nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0);
    if (agg === 'count') return String(nums.length);
    if (agg === 'min') return String(nums.length ? Math.min(...nums) : 0);
    if (agg === 'max') return String(nums.length ? Math.max(...nums) : 0);
    return '0';
  });
}

/**
 * Evaluate a formula safely.
 * @param {string} expr - the formula, e.g. "{weight} * 703 / ({height} * {height})"
 * @param {object} scope - { fields: {id: val}, row?: {col: val}, allRows?: [row,...] }
 * @returns {number|null} — the computed number, or null if formula is empty or invalid.
 */
export function evaluateFormula(expr, scope = {}) {
  if (!expr || typeof expr !== 'string' || !expr.trim()) return null;
  let src = expr.trim();
  const fields = scope.fields || {};
  // 1. Date helpers (consume {field} refs that should stay ISO strings, not numeric)
  src = substituteDateHelpers(src, fields);
  // 2. Cross-table dot-refs (must run BEFORE bare {field_id} substitution)
  src = substituteTableRefs(src, fields);
  // 3. Local row / column aggregates (only meaningful in calc_table cells)
  if (scope.row) src = substituteRowRefs(src, scope.row);
  if (scope.allRows) src = substituteColRefs(src, scope.allRows);
  // 4. Bare {field_id} refs (numeric)
  src = substituteFieldRefs(src, fields);

  if (DISALLOWED.test(src)) return null;

  try {
    // Use Function constructor with no globals binding — pass only Math.
    // eslint-disable-next-line no-new-func
    const fn = new Function('Math', `"use strict"; return (${src});`);
    const result = fn({ round: Math.round, floor: Math.floor, ceil: Math.ceil, abs: Math.abs, max: Math.max, min: Math.min, sqrt: Math.sqrt, pow: Math.pow, log: Math.log, exp: Math.exp });
    if (typeof result === 'number' && isFinite(result)) return result;
    return null;
  } catch {
    return null;
  }
}

/**
 * Format a computed numeric result for display.
 * @param {number|null} val
 * @param {object} fmt - { type: 'number'|'integer'|'currency'|'percent', decimals?: number }
 */
export function formatComputed(val, fmt = {}) {
  if (val === null || val === undefined || !isFinite(val)) return '';
  const decimals = fmt.decimals === undefined ? 2 : fmt.decimals;
  if (fmt.type === 'integer') return String(Math.round(val));
  if (fmt.type === 'currency') return val.toLocaleString('en-US', { style: 'currency', currency: fmt.currency || 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  if (fmt.type === 'percent') return `${(val * 100).toFixed(decimals)}%`;
  return val.toFixed(decimals);
}

/**
 * Compute per-row auto values for a calc_table config, given its current grid data.
 * Returns a new grid with formula cells filled in.
 *   config.columns: [{key, label, type: 'text'|'number'|'formula', formula?, format?}]
 *   rows: [{col_key: value, ...}, ...]
 *   extraScope.fields — outer form responses
 */
export function computeTableRows(config, rows, extraScope = {}) {
  const cols = config?.columns || [];
  const filled = (rows || []).map(r => ({ ...r }));
  // Multi-pass to allow formula-on-formula within the same row (bounded to 3 passes)
  for (let pass = 0; pass < 3; pass++) {
    let changed = false;
    for (const row of filled) {
      for (const col of cols) {
        if (col.type !== 'formula' || !col.formula) continue;
        const newVal = evaluateFormula(col.formula, { row, allRows: filled, fields: extraScope.fields || {} });
        if (newVal !== row[col.key]) { row[col.key] = newVal; changed = true; }
      }
    }
    if (!changed) break;
  }
  return filled;
}

/**
 * Compute the bottom totals row for a calc_table if enabled.
 *   config.totals: { enabled: bool, cells: { <col_key>: 'sum'|'avg'|'count'|'min'|'max'|'formula', formula?: string, format?: {...} } }
 */
export function computeTotalsRow(config, rows, extraScope = {}) {
  if (!config?.totals?.enabled) return null;
  const out = {};
  const cells = config.totals.cells || {};
  for (const [key, spec] of Object.entries(cells)) {
    const agg = typeof spec === 'string' ? spec : spec.agg;
    if (agg === 'formula') {
      out[key] = evaluateFormula(spec.formula || '', { allRows: rows || [], fields: extraScope.fields || {} });
    } else {
      const nums = (rows || []).map(r => safeNumber(r[key]));
      if (agg === 'sum') out[key] = nums.reduce((a, b) => a + b, 0);
      else if (agg === 'avg') out[key] = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
      else if (agg === 'count') out[key] = nums.length;
      else if (agg === 'min') out[key] = nums.length ? Math.min(...nums) : 0;
      else if (agg === 'max') out[key] = nums.length ? Math.max(...nums) : 0;
    }
  }
  return out;
}


// -----------------------------------------------------------------------------
//  Formula Helper — field picker + preset library (used by the form builder UI)
// -----------------------------------------------------------------------------

/**
 * Pre-canned healthcare + general formulas selectable from the builder UI.
 * Each preset lists the field refs it expects — the user can wire them to
 * actual form fields by editing the generated expression.
 */
export const FORMULA_PRESETS = [
  {
    name: 'BMI (metric)',
    category: 'Clinical',
    expression: '{weight_kg} / (({height_cm}/100) * ({height_cm}/100))',
    inputs: ['weight_kg', 'height_cm'],
    description: 'Body Mass Index — kg / m²',
    format: { type: 'number', decimals: 1 },
  },
  {
    name: 'BMI (imperial)',
    category: 'Clinical',
    expression: '{weight_lb} * 703 / ({height_in} * {height_in})',
    inputs: ['weight_lb', 'height_in'],
    description: 'Body Mass Index — lb / in² × 703',
    format: { type: 'number', decimals: 1 },
  },
  {
    name: 'BSA (Mosteller)',
    category: 'Clinical',
    expression: 'Math.sqrt({height_cm} * {weight_kg} / 3600)',
    inputs: ['height_cm', 'weight_kg'],
    description: 'Body Surface Area — √(h·w / 3600)',
    format: { type: 'number', decimals: 2 },
  },
  {
    name: 'BSA (Du Bois)',
    category: 'Clinical',
    expression: '0.007184 * Math.pow({height_cm}, 0.725) * Math.pow({weight_kg}, 0.425)',
    inputs: ['height_cm', 'weight_kg'],
    description: 'Body Surface Area — Du Bois formula',
    format: { type: 'number', decimals: 2 },
  },
  {
    name: 'Mean Arterial Pressure',
    category: 'Clinical',
    expression: '({systolic} + 2 * {diastolic}) / 3',
    inputs: ['systolic', 'diastolic'],
    description: 'MAP — (SBP + 2·DBP) / 3',
    format: { type: 'number', decimals: 0 },
  },
  {
    name: 'Age from DOB',
    category: 'Date',
    expression: 'AGE({dob})',
    inputs: ['dob'],
    description: 'Whole years from date-of-birth to today',
    format: { type: 'integer' },
  },
  {
    name: 'Days between dates',
    category: 'Date',
    expression: 'DAYS_BETWEEN({start_date}, {end_date})',
    inputs: ['start_date', 'end_date'],
    description: 'Whole days (end − start)',
    format: { type: 'integer' },
  },
  {
    name: 'Length of stay',
    category: 'Date',
    expression: 'DAYS_BETWEEN({admission_date}, {discharge_date})',
    inputs: ['admission_date', 'discharge_date'],
    description: 'Days from admission to discharge',
    format: { type: 'integer' },
  },
  {
    name: 'Line total (qty × price)',
    category: 'Billing',
    expression: 'row.qty * row.price',
    inputs: [],
    description: 'For calc_table formula column — multiply two columns',
    context: 'row',
    format: { type: 'currency', decimals: 2 },
  },
  {
    name: 'Discounted total',
    category: 'Billing',
    expression: 'row.qty * row.price * (1 - row.discount/100)',
    inputs: [],
    description: 'Line item with percent discount',
    context: 'row',
    format: { type: 'currency', decimals: 2 },
  },
  {
    name: 'Grand total (from another table)',
    category: 'Billing',
    expression: '{other_table_id.col.total.sum}',
    inputs: ['other_table_id'],
    description: 'Sum up a column in a different calc_table',
    format: { type: 'currency', decimals: 2 },
  },
  {
    name: 'Conditional (greater than)',
    category: 'Logic',
    expression: '{a} > {b} ? {a} : {b}',
    inputs: ['a', 'b'],
    description: 'Ternary — return the larger value',
    format: { type: 'number' },
  },
];

/**
 * Build a list of all valid field references available in the current form,
 * for insertion from the builder UI.  Returns:
 *   [{ label, insert, group }]
 *
 * `label` is human-readable ("Weight (kg)"), `insert` is the exact string
 * to inject into the formula (e.g. "{weight_kg}" or "{dose_table.col.total.sum}").
 */
export function getFieldRefs(allFields = [], { includeCalcTableRefs = true } = {}) {
  const refs = [];
  for (const f of allFields || []) {
    const fid = f.field_id;
    if (!fid) continue;
    const ft = f.field_type;
    const lbl = f.label || fid;
    if (ft === 'section' || ft === 'info' || ft === 'reminder') continue;
    if (ft === 'calc_table') {
      if (!includeCalcTableRefs) continue;
      const cols = (f.calc_table_config?.columns || []).filter(c => c.type !== 'text');
      for (const c of cols) {
        for (const agg of ['sum', 'avg', 'count', 'min', 'max']) {
          refs.push({
            group: `${lbl} (table)`,
            label: `${c.label || c.key} — ${agg}`,
            insert: `{${fid}.col.${c.key}.${agg}}`,
          });
        }
      }
      continue;
    }
    // Non-table numeric / date fields
    const numericTypes = ['number', 'calculated'];
    const dateTypes = ['date'];
    const textishTypes = ['text', 'textarea', 'select', 'radio'];
    if (numericTypes.includes(ft)) {
      refs.push({ group: 'Numeric fields', label: `${lbl}`, insert: `{${fid}}` });
    } else if (dateTypes.includes(ft)) {
      refs.push({ group: 'Date fields', label: `${lbl} (date)`, insert: `{${fid}}` });
      refs.push({ group: 'Date fields', label: `AGE(${lbl})`, insert: `AGE({${fid}})` });
    } else if (textishTypes.includes(ft)) {
      // Still expose — will be safeNumber-coerced to 0 for text, but useful for numeric strings
      refs.push({ group: 'Other fields', label: `${lbl}`, insert: `{${fid}}` });
    }
  }
  return refs;
}
